/**
 * BillingIdentitySection — per-account billing identity on the Agreement Fees tab
 * (per-account fee/billing arc, Inc 2b).
 *
 * Own Read/Edit transaction (§3g Pattern A), separate from FeesSection's fee-config
 * transaction. Read shows one row per currently-related account (direct + indirect),
 * sorted direct-first then alphabetically, with the billing identity (name / TIN /
 * address), per-field drift markers and a completeness flag. Edit stages manual
 * field edits AND snapshot-from-party intents, all committing on Save; the dirty-form
 * guard blocks navigation away from a dirty transaction.
 *
 * Snapshot is STAGED (Pattern A), not immediate: picking a party pre-fills the
 * editable fields with a preview of what will be copied AND marks the row as a
 * snapshot. On Save we POST .../snapshot (the server does the authoritative copy —
 * legal vs natural — and records billing_person_id as the drift source); if the user
 * also edited the fields, a follow-up PUT applies those overrides (intentional drift).
 * The preview mapping mirrors the backend for DISPLAY only — drift + completeness are
 * SERVER-DERIVED (BillingIdentityRow), never recomputed here (point 2; C-214).
 *
 * Read is visible to any signed-in user who can see the agreement; editing (Edit
 * button, snapshot, manual save, fee-schedule/VAT assignment) is Admin-only — the
 * backend enforces this (GET = any user, PUT/POST = Admin). Non-admins see the
 * list with an "Editing is Admin-only" note instead of the Edit button.
 *
 * Fee-schedule + VAT assignment (Fee Schedule model, Slice 3d/4; C-244): each
 * row also carries the account's own fee-schedule pick + VAT — a THIRD
 * independent staged-edit axis alongside manual identity edits and snapshots,
 * committed via its own PUT .../assignment call in the same batched Save. The
 * picker is pre-filtered server-side to schedules available to THIS agreement
 * (design §3.7) — nothing here re-derives availability client-side.
 */
import { useMemo, useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Group, NumberInput, Paper, Select, Stack,
  Table, Text, TextInput, Title, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAddressBook, IconAlertTriangle, IconArrowBackUp, IconCheck, IconCircleX,
  IconExternalLink, IconPencil, IconUser, IconX,
} from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { ApiError } from '../api/client';
import { accountBillingApi, type AccountFeePreview, type BillingIdentityRow } from '../api/accountBilling';
import { agreementPartiesApi } from '../api/agreementParties';
import { bpsToPct, feeSchedulesApi, fmtEurFromCents, pctToBps } from '../api/feeSchedules';
import { personsApi } from '../api/persons';
import { EntityPicker } from './EntityPicker';
import { useDirtyFormGuard } from '../hooks/useDirtyFormGuard';
import { useCurrentUser } from '../hooks/useAuth';
import type { Person, UUID } from '../api/types';

const BILLING_KEY = (id: string) => ['account-billing', id] as const;
const FEE_PREVIEW_KEY = (id: string) => ['account-fee-preview', id] as const;
const AVAILABLE_SCHEDULES_KEY = (agreementId: string) => ['fee-schedules', 'available', agreementId] as const;

// Render an ISO period_month (YYYY-MM-DD) as "MMM-YYYY" for the "priced at" caption
// (A4, 2026-07-02) so it doesn't read like a specific day (2026-06-01).
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtMonthYear(iso: string): string {
  const [y, m] = iso.split('-');
  const mi = Number(m) - 1;
  return `${MONTH_ABBR[mi] ?? m}-${y}`;
}

// Per-account billing cadence (Slice-4 #2/#7): frequency lives on the assignment,
// beside VAT — not on the schedule.
const FREQUENCY_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semiannual', label: 'Semiannual' },
];
const FREQ_LABEL: Record<string, string> = {
  monthly: 'Monthly', quarterly: 'Quarterly', semiannual: 'Semiannual',
};

/** Per-row staged edits. Presence of an entry = "touched this row". */
interface RowDraft {
  billing_name: string;
  billing_tin: string;
  billing_address: string;
  snapshotPersonId: UUID | null;      // staged snapshot intent (server copies on Save)
  snapshotPersonName: string | null;  // for the "Snapshot from <name>" chip
  snapshotEdited: boolean;            // user tweaked the fields after staging a snapshot
  feeScheduleId: UUID | null;         // staged fee-schedule assignment (null = unassigned)
  vatPct: number | '';                // staged VAT (%); '' while the field is empty
  feeFrequency: string | null;        // staged per-account billing cadence (null = unset)
}

function seedDraft(row: BillingIdentityRow): RowDraft {
  return {
    billing_name: row.billing_name ?? '',
    billing_tin: row.billing_tin ?? '',
    billing_address: row.billing_address ?? '',
    snapshotPersonId: null,
    snapshotPersonName: null,
    snapshotEdited: false,
    feeScheduleId: row.fee_schedule_id,
    vatPct: row.vat_rate_bps != null ? bpsToPct(row.vat_rate_bps) : '',
    feeFrequency: row.fee_frequency,
  };
}

function assignmentChanged(row: BillingIdentityRow, d: RowDraft): boolean {
  const draftBps = d.vatPct === '' ? null : pctToBps(d.vatPct);
  return d.feeScheduleId !== row.fee_schedule_id
    || draftBps !== row.vat_rate_bps
    || d.feeFrequency !== row.fee_frequency;
}

/** PREVIEW-ONLY mapping — mirrors backend account_billing._person_billing_fields +
 *  _compose_address so the user sees what a snapshot will copy. The SAVE is
 *  server-authoritative (POST snapshot). #70: full delimited address (street |
 *  postal | city | state/country); a party with no address previews blank (#71). */
function composeAddress(...parts: (string | null | undefined)[]): string {
  return parts.map((p) => (p ?? '').trim()).filter(Boolean).join(' | ');
}
function previewFromPerson(p: Person): { name: string; tin: string; address: string } {
  const legal = p.kind === 'LEGAL';
  return {
    name: p.display_name ?? '',
    tin: (legal ? p.tin : p.tin_1) ?? '',
    address: legal
      ? composeAddress(p.registered_office_address, p.registered_office_postal_code,
          p.registered_office_city, p.registered_office_state, p.registered_office_country)  // #69 (0070)
      : composeAddress(p.fiscal_address, p.fiscal_address_postal_code,
          p.fiscal_address_city, p.fiscal_address_state, p.fiscal_address_country),  // #69 (0070)
  };
}

function manualChanged(row: BillingIdentityRow, d: RowDraft): boolean {
  return (
    d.billing_name !== (row.billing_name ?? '') ||
    d.billing_tin !== (row.billing_tin ?? '') ||
    d.billing_address !== (row.billing_address ?? '')
  );
}

function rowChanged(row: BillingIdentityRow, d: RowDraft): boolean {
  return d.snapshotPersonId != null || manualChanged(row, d) || assignmentChanged(row, d);
}

function accountLabel(row: BillingIdentityRow): string {
  const bank = row.bank_name || 'Account';
  return row.account_number ? `${bank} — ${row.account_number}` : bank;
}

export function BillingIdentitySection({
  agreementId, readOnly = false,
}: { agreementId: string; readOnly?: boolean }) {
  const qc = useQueryClient();
  const { user } = useCurrentUser();
  const isAdmin = user?.role === 'admin';

  // Read is allowed for any signed-in user (backend GET = any user); editing is
  // Admin-only (backend PUT/POST = Admin). So the list loads for everyone.
  const q = useQuery({
    queryKey: BILLING_KEY(agreementId),
    queryFn: () => accountBillingApi.list(agreementId),
  });

  // Direct accounts first, then indirect; alphabetical within each (Pedro 2026-06-22).
  const rows = useMemo(
    () => [...(q.data ?? [])].sort((a, b) =>
      (Number(b.is_direct) - Number(a.is_direct))
      || (a.bank_name ?? '').localeCompare(b.bank_name ?? '')
      || (a.account_number ?? '').localeCompare(b.account_number ?? '')),
    [q.data],
  );

  // Schedules this agreement may assign (server-filtered: Standard + any Custom
  // available_to_all or granted here — design §3.7). Read-open, so any user's
  // Read view can also show the assigned schedule's name.
  const availableQ = useQuery({
    queryKey: AVAILABLE_SCHEDULES_KEY(agreementId),
    queryFn: () => feeSchedulesApi.availableForAgreement(agreementId),
  });
  const scheduleOptions = useMemo(
    () => (availableQ.data ?? []).map((s) => ({ value: s.id, label: s.name ?? '(unnamed)' })),
    [availableQ.data],
  );

  // Read-mode resulting fee per account (Slice 5.6 (a)): saved assignments priced at
  // the latest month with AuM. Per-account tolerant (missing AuM -> absent -> "—").
  const previewQ = useQuery({
    queryKey: FEE_PREVIEW_KEY(agreementId),
    queryFn: () => accountBillingApi.feePreview(agreementId),
  });
  const previewByAccount = useMemo(
    () => new Map<string, AccountFeePreview>((previewQ.data ?? []).map((p) => [p.account_id, p])),
    [previewQ.data],
  );

  const [editMode, setEditMode] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({});
  const [saving, setSaving] = useState(false);

  const dirty = useMemo(() => {
    if (!editMode) return false;
    return rows.some((r) => {
      const d = drafts[r.account_id];
      return d ? rowChanged(r, d) : false;
    });
  }, [editMode, rows, drafts]);
  // Pedro 2026-10-10: leaving with unsaved edits offers "Save & leave" (runs save() below,
  // which reports true only when every row saved).
  const confirmDiscard = useDirtyFormGuard(dirty, save);

  const enter = () => { setDrafts({}); setEditMode(true); };
  const leave = () => { setDrafts({}); setEditMode(false); };

  // Apply-to-all (Slice-4 #7): set a schedule + frequency + VAT once and STAGE them
  // on every account row (override individuals before Save). Nothing commits here —
  // the normal batched Save persists whatever ended up changed.
  const [bulkScheduleId, setBulkScheduleId] = useState<UUID | null>(null);
  const [bulkFreq, setBulkFreq] = useState<string | null>(null);
  const [bulkVat, setBulkVat] = useState<number | ''>('');
  const applyToAll = () =>
    setDrafts((prev) => {
      const next = { ...prev };
      for (const r of rows) {
        const base = next[r.account_id] ?? seedDraft(r);
        next[r.account_id] = { ...base, feeScheduleId: bulkScheduleId, feeFrequency: bulkFreq, vatPct: bulkVat };
      }
      return next;
    });

  const patchRow = (row: BillingIdentityRow, p: Partial<RowDraft>) =>
    setDrafts((prev) => {
      const base = prev[row.account_id] ?? seedDraft(row);
      return { ...prev, [row.account_id]: { ...base, ...p } };
    });

  /** Edit a billing field; if a snapshot is staged on this row, mark it edited so
   *  Save applies the override (intentional drift) after the snapshot copy. */
  const editField = (row: BillingIdentityRow, key: 'billing_name' | 'billing_tin' | 'billing_address', value: string) => {
    const staged = (drafts[row.account_id] ?? seedDraft(row)).snapshotPersonId != null;
    patchRow(row, { [key]: value, ...(staged ? { snapshotEdited: true } : {}) } as Partial<RowDraft>);
  };

  const stageSnapshot = async (row: BillingIdentityRow, personId: UUID, name: string) => {
    try {
      const p = await personsApi.get(personId);
      const pv = previewFromPerson(p);
      patchRow(row, {
        billing_name: pv.name, billing_tin: pv.tin, billing_address: pv.address,
        snapshotPersonId: personId, snapshotPersonName: name, snapshotEdited: false,
      });
    } catch (e) {
      notifications.show({ color: 'red', message: e instanceof ApiError ? e.detail : 'Could not load the selected party.' });
    }
  };

  /** Cancel a staged snapshot (and any edits made after it) — revert to stored. */
  const clearSnapshot = (row: BillingIdentityRow) =>
    setDrafts((prev) => { const n = { ...prev }; delete n[row.account_id]; return n; });

  const setFeeSchedule = (row: BillingIdentityRow, id: UUID | null) => patchRow(row, { feeScheduleId: id });
  const setVat = (row: BillingIdentityRow, pct: number | '') => patchRow(row, { vatPct: pct });
  const setFeeFrequency = (row: BillingIdentityRow, v: string | null) => patchRow(row, { feeFrequency: v });

  async function save(): Promise<boolean> {
    const ops: { accountId: string; run: () => Promise<unknown> }[] = [];
    for (const r of rows) {
      const d = drafts[r.account_id];
      if (!d) continue;
      if (d.snapshotPersonId) {
        const pid = d.snapshotPersonId;
        const edited = d.snapshotEdited;
        const vals = {
          billing_name: d.billing_name.trim() || null,
          billing_tin: d.billing_tin.trim() || null,
          billing_address: d.billing_address.trim() || null,
        };
        ops.push({
          accountId: r.account_id,
          run: async () => {
            await accountBillingApi.snapshot(agreementId, r.account_id, pid);
            if (edited) await accountBillingApi.update(agreementId, r.account_id, vals);
          },
        });
      } else if (manualChanged(r, d)) {
        ops.push({
          accountId: r.account_id,
          run: () => accountBillingApi.update(agreementId, r.account_id, {
            billing_name: d.billing_name.trim() || null,
            billing_tin: d.billing_tin.trim() || null,
            billing_address: d.billing_address.trim() || null,
          }),
        });
      }
      // Fee-schedule/VAT assignment is a THIRD, independent axis — it can fire
      // alongside either identity op above (the backend endpoints are separate,
      // so a row can carry both an identity op and an assignment op).
      if (assignmentChanged(r, d)) {
        ops.push({
          accountId: r.account_id,
          run: () => accountBillingApi.setAssignment(agreementId, r.account_id, {
            fee_schedule_id: d.feeScheduleId,
            vat_rate_bps: d.vatPct === '' ? null : pctToBps(d.vatPct),
            fee_frequency: d.feeFrequency,
          }),
        });
      }
    }
    if (ops.length === 0) { leave(); return true; }

    setSaving(true);
    // Keyed by accountId (a row may carry 2 ops) — a row is only "clean" once
    // EVERY op targeting it has succeeded (§3g: not un-dirty until everything did).
    const failedIds = new Set<string>();
    const succeededIds = new Set<string>();
    for (const op of ops) {
      try { await op.run(); succeededIds.add(op.accountId); }
      catch (e) {
        failedIds.add(op.accountId);
        notifications.show({
          color: 'red', title: 'Could not save one row', autoClose: false,
          message: e instanceof ApiError ? e.detail : 'Unknown error',
        });
      }
    }
    const cleanIds = [...succeededIds].filter((id) => !failedIds.has(id));
    await qc.invalidateQueries({ queryKey: BILLING_KEY(agreementId) });
    // C2/D4 (2026-07-02): the Resulting-fee column is a separate query — invalidate it
    // too so an assignment change reflects on Save, not only after a hard refresh.
    await qc.invalidateQueries({ queryKey: FEE_PREVIEW_KEY(agreementId) });
    setSaving(false);

    if (failedIds.size === 0) {
      notifications.show({ color: 'green', message: `Billing details saved (${cleanIds.length} updated).` });
      leave();
    } else {
      // §3g: not un-dirty until everything succeeded — drop only the rows where
      // EVERY op succeeded; a row with one op failed stays staged for a retry.
      setDrafts((prev) => {
        const next = { ...prev };
        for (const id of cleanIds) delete next[id];
        return next;
      });
      notifications.show({
        color: 'orange', title: 'Saved with errors',
        message: `${cleanIds.length} row(s) fully saved, ${failedIds.size} row(s) have at least one failed change — those stay staged. Fix and Save again.`,
      });
    }
    return failedIds.size === 0;
  }

  const anyDrift = rows.some((r) => r.name_drift || r.tin_drift || r.address_drift);

  return (
    <Stack mt="xl">
      <Group justify="space-between">
        <Group gap="xs">
          <IconAddressBook size={20} />
          <Title order={4}>Billing details</Title>
          <Text size="sm" c="dimmed">one legal payee per related account</Text>
        </Group>
        {readOnly ? (
          isAdmin ? (
            <Button component={Link} to={`/admin/fees?tab=billing-details&agreement=${agreementId}`}
              variant="light" leftSection={<IconExternalLink size={16} />}>
              Manage in Admin → Fees
            </Button>
          ) : (
            <Text size="sm" c="dimmed">Managed in Admin → Fees</Text>
          )
        ) : !editMode ? (
          isAdmin ? (
            <Button leftSection={<IconPencil size={16} />} onClick={enter} disabled={q.isLoading || rows.length === 0}>
              Edit
            </Button>
          ) : (
            <Text size="sm" c="dimmed">Editing is Admin-only</Text>
          )
        ) : (
          <Group gap="xs">
            <Button variant="default" leftSection={<IconArrowBackUp size={16} />}
              onClick={() => confirmDiscard(leave)}>Discard</Button>
            <Button color="azul" loading={saving} onClick={save}>Save changes</Button>
          </Group>
        )}
      </Group>
      {readOnly && (
        <Text size="xs" c="dimmed">
          Read-only mirror — billing identity and fee-schedule assignment are managed
          firm-wide in Admin → Fees → Billing details.
        </Text>
      )}

      {q.isLoading && <Text c="dimmed">Loading…</Text>}
      {!q.isLoading && rows.length === 0 && (
        <Alert color="gray" variant="light">
          No accounts are currently related to this agreement. A billing row appears
          once an account is linked — directly, or indirectly through an insurance policy.
        </Alert>
      )}

      {/* ── READ ──────────────────────────────────────────────────────── */}
      {!editMode && rows.length > 0 && (
        <Table withTableBorder striped>
          <Table.Thead><Table.Tr>
            <Table.Th>Account</Table.Th>
            <Table.Th>Billing name</Table.Th>
            <Table.Th>Billing TIN</Table.Th>
            <Table.Th>Billing address</Table.Th>
            <Table.Th w={130}>Completeness</Table.Th>
            <Table.Th>Fee schedule</Table.Th>
            <Table.Th w={110}>Frequency</Table.Th>
            <Table.Th w={90}>VAT</Table.Th>
            <Table.Th w={140}>Resulting fee</Table.Th>
          </Table.Tr></Table.Thead>
          <Table.Tbody>
            {rows.map((r) => (
              <Table.Tr key={r.account_id}>
                <Table.Td><AccountCell row={r} /></Table.Td>
                <Table.Td><FieldValue value={r.billing_name} drift={r.name_drift} /></Table.Td>
                <Table.Td><FieldValue value={r.billing_tin} drift={r.tin_drift} /></Table.Td>
                <Table.Td><FieldValue value={r.billing_address} drift={r.address_drift} /></Table.Td>
                <Table.Td>
                  {r.is_complete ? (
                    <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>Complete</Badge>
                  ) : (
                    <Badge color="orange" variant="light" leftSection={<IconCircleX size={12} />}>Incomplete</Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  <Stack gap={2}>
                    {r.fee_schedule_name
                      ? <Text size="sm">{r.fee_schedule_name}</Text>
                      : <Text size="sm" c="dimmed">Unassigned</Text>}
                    {r.assigned_not_in_basis && (
                      <Tooltip withArrow multiline w={260}
                        label="Billed under this schedule but NOT part of its basis group — it pays the family rate without feeding it.">
                        <Badge size="xs" color="grape" variant="light">not in basis</Badge>
                      </Tooltip>
                    )}
                    {r.in_basis_billed_elsewhere && (
                      <Tooltip withArrow multiline w={260}
                        label="This account feeds another schedule's basis group but is billed under a different schedule.">
                        <Badge size="xs" color="grape" variant="light">feeds another basis</Badge>
                      </Tooltip>
                    )}
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{r.fee_frequency ? (FREQ_LABEL[r.fee_frequency] ?? r.fee_frequency) : '—'}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{r.vat_rate_bps != null ? `${bpsToPct(r.vat_rate_bps).toFixed(2)}%` : '—'}</Text>
                </Table.Td>
                <Table.Td>
                  {(() => {
                    const p = previewByAccount.get(r.account_id);
                    if (!p) return <Text size="sm" c="dimmed">—</Text>;
                    return (
                      <Stack gap={0}>
                        <Text size="sm">€{fmtEurFromCents(p.net_fee_cents)}</Text>
                        <Text size="xs" c="dimmed">
                          {p.effective_rate_bps != null
                            ? `${bpsToPct(p.effective_rate_bps).toFixed(2)}%/yr`
                            : 'fixed'}
                        </Text>
                      </Stack>
                    );
                  })()}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      {!editMode && anyDrift && (
        <Text size="xs" c="orange">
          Orange-marked fields differ from the linked party's current value (drift). Re-snapshot to refresh, or keep the override.
        </Text>
      )}
      {!editMode && rows.length > 0 && (previewQ.data?.length ?? 0) > 0 && (
        <Text size="xs" c="dimmed">
          Resulting fee = net (ex-VAT) per billing period, priced at the latest month with
          AuM ({fmtMonthYear(previewQ.data![0].period_month)}); the %/yr is the annualised
          effective rate. Accounts without AuM for that month show "—".
        </Text>
      )}

      {/* ── EDIT (Admin only) ──────────────────────────────────────────── */}
      {editMode && (
        <Stack gap="md">
          <Paper withBorder p="md" radius="md" bg="var(--mantine-color-gray-0)">
            <Group align="flex-end" gap="sm" wrap="wrap">
              <Text size="sm" fw={500}>Apply to all accounts:</Text>
              <Select label="Fee schedule" w={220} data={scheduleOptions}
                value={bulkScheduleId} onChange={(v) => setBulkScheduleId(v)} clearable
                placeholder={availableQ.isLoading ? 'Loading…' : 'Unassigned'} />
              <Select label="Frequency" w={140} data={FREQUENCY_OPTIONS}
                value={bulkFreq} onChange={(v) => setBulkFreq(v)} clearable placeholder="—" />
              <NumberInput label="VAT" w={110} min={0} max={100} step={0.01} decimalScale={2} suffix=" %"
                value={bulkVat} onChange={(v) => setBulkVat(typeof v === 'number' ? v : '')} />
              <Button variant="light" onClick={applyToAll} disabled={rows.length === 0}>Apply to all</Button>
            </Group>
            <Text size="xs" c="dimmed" mt={4}>
              Stages the schedule, frequency and VAT on every account below — override
              individuals before saving. Nothing is saved until <b>Save changes</b>.
            </Text>
          </Paper>
          {rows.some((r) => { const d = drafts[r.account_id]; return d ? assignmentChanged(r, d) : false; }) && (
            <Alert color="blue" variant="light" icon={<IconAlertTriangle size={16} />}>
              Changing a fee-schedule assignment recomputes the shared rate for the
              co-assigned accounts on that schedule — the exact fees are set at the next compute.
            </Alert>
          )}
          {rows.map((r) => {
            const d = drafts[r.account_id] ?? seedDraft(r);
            const snap = d.snapshotPersonId != null;
            return (
              <Paper key={r.account_id} withBorder p="md" radius="md">
                <Stack gap="sm">
                  <AccountCell row={r} />

                  {snap && (
                    <Group gap="xs">
                      <Badge color="orange" variant="light" leftSection={<IconUser size={12} />}>
                        Snapshot from {d.snapshotPersonName || 'party'}
                      </Badge>
                      <Tooltip label="Cancel snapshot — revert to the saved values" withArrow>
                        <ActionIcon variant="subtle" color="gray" size="sm"
                          aria-label="Cancel snapshot" onClick={() => clearSnapshot(r)}>
                          <IconX size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Text size="xs" c="dimmed">Values below will be saved (server copies on Save; editable here).</Text>
                    </Group>
                  )}

                  <Group align="flex-end" gap="sm" wrap="wrap">
                    <TextInput label="Billing name" value={d.billing_name} w={220}
                      onChange={(e) => editField(r, 'billing_name', e.currentTarget.value)} />
                    <TextInput label="Billing TIN" value={d.billing_tin} w={160}
                      onChange={(e) => editField(r, 'billing_tin', e.currentTarget.value)} />
                    <TextInput label="Billing address" value={d.billing_address} w={280}
                      onChange={(e) => editField(r, 'billing_address', e.currentTarget.value)} />
                    <div style={{ minWidth: 240 }}>
                      <Text size="sm" fw={500} mb={4}>{snap ? 'Re-snapshot from a party' : 'Snapshot from a party'}</Text>
                      <RelatedPartyPicker agreementId={agreementId}
                        onPick={(pid, name) => { void stageSnapshot(r, pid, name); }} />
                    </div>
                  </Group>

                  <Group align="flex-end" gap="sm" wrap="wrap">
                    <Select
                      label="Fee schedule" w={260}
                      placeholder={availableQ.isLoading ? 'Loading…' : 'Unassigned'}
                      data={scheduleOptions}
                      value={d.feeScheduleId}
                      onChange={(v) => setFeeSchedule(r, v)}
                      clearable
                      description="Only schedules available to this agreement are offered."
                    />
                    <Select
                      label="Billing frequency" w={150}
                      placeholder="—" data={FREQUENCY_OPTIONS}
                      value={d.feeFrequency}
                      onChange={(v) => setFeeFrequency(r, v)}
                      clearable
                      description="How often this account is billed."
                    />
                    <NumberInput
                      label="VAT" w={120} min={0} max={100} step={0.01} decimalScale={2} suffix=" %"
                      value={d.vatPct}
                      onChange={(v) => setVat(r, typeof v === 'number' ? v : '')}
                    />
                  </Group>
                </Stack>
              </Paper>
            );
          })}
          <Text size="xs" c="dimmed">
            Manual edits, snapshots, and the fee-schedule/VAT assignment are all staged —
            nothing is saved until you press <b>Save changes</b>. A snapshot copies the
            party's current name / TIN / address (server-side) when you save.
          </Text>
        </Stack>
      )}
    </Stack>
  );
}

/** The account identity cell — bank · number, direct/indirect, insurer·policy, status. */
function AccountCell({ row }: { row: BillingIdentityRow }) {
  return (
    <Stack gap={2}>
      <Group gap="xs">
        <Text size="sm" fw={500}>{accountLabel(row)}</Text>
        <Badge size="xs" variant="light" color={row.is_direct ? 'blue' : 'grape'}>
          {row.is_direct ? 'Direct' : 'Indirect'}
        </Badge>
        <Badge size="xs" variant="light" color={row.status === 'Active' ? 'green' : 'gray'}>
          {row.status}
        </Badge>
      </Group>
      {!row.is_direct && (row.insurer || row.policy_number) && (
        <Text size="xs" c="dimmed">
          {row.insurer ?? 'Insurer'}{row.policy_number ? ` · ${row.policy_number}` : ''}
        </Text>
      )}
    </Stack>
  );
}

/** A read-mode billing field value, with an orange drift marker when it differs
 *  from the linked party's current value. Empty renders a dimmed em-dash. */
function FieldValue({ value, drift }: { value: string | null; drift: boolean }) {
  return (
    <Group gap={6} wrap="nowrap">
      {value ? <Text size="sm">{value}</Text> : <Text size="sm" c="dimmed">—</Text>}
      {drift && (
        <Tooltip label="Differs from the linked party's current value" withArrow>
          <Badge size="xs" color="orange" variant="light" leftSection={<IconAlertTriangle size={10} />}>drift</Badge>
        </Tooltip>
      )}
    </Group>
  );
}

/** One-shot person picker scoped to the agreement's related parties (holders /
 *  representatives / beneficiaries). Reuses EntityPicker (open-on-click, C-183).
 *  De-dupes a person who holds multiple roles; calls onPick(personId, name). */
function RelatedPartyPicker({
  agreementId, onPick,
}: { agreementId: string; onPick: (personId: UUID, name: string) => void }) {
  const partiesQ = useQuery({
    queryKey: ['agreement-party-persons', agreementId],
    queryFn: () => agreementPartiesApi.list({ agreement_id: agreementId, limit: 200 }),
  });

  const people = useMemo(() => {
    const seen = new Map<string, { id: UUID; display_name: string; role: string }>();
    for (const p of partiesQ.data?.items ?? []) {
      if (!p.person_id) continue;
      const ex = seen.get(p.person_id);
      if (ex) { if (!ex.role.includes(p.role)) ex.role = `${ex.role}, ${p.role}`; }
      else seen.set(p.person_id, { id: p.person_id, display_name: p.person_display_name ?? '(unnamed party)', role: p.role });
    }
    return [...seen.values()];
  }, [partiesQ.data]);

  return (
    <EntityPicker<{ id: UUID; display_name: string; role: string }>
      placeholder={people.length ? 'Pick a party…' : 'No parties on this agreement'}
      value={null}
      onChange={(id) => {
        if (!id) return;
        const m = people.find((x) => x.id === id);
        onPick(id, m?.display_name ?? '');
      }}
      search={async (query) => {
        const ql = query.trim().toLowerCase();
        const items = ql ? people.filter((x) => x.display_name.toLowerCase().includes(ql)) : people;
        return { items, total: items.length, limit: 200, offset: 0 };
      }}
      getById={async (id) => people.find((x) => x.id === id) ?? { id, display_name: '', role: '' }}
      formatLabel={(r) => r.display_name}
      formatDetail={(r) => r.role || undefined}
      entityNoun="parties on this agreement"
    />
  );
}
